<template>
  <div>
    <div class="mb-3">
      <h5 class="mb-0 font-weight-bold">Sprint Setting</h5>
      <p class="text-secondary text-sm mb-0">
        Configure team sprints and sprint schedules.
      </p>
    </div>

    <div class="row">
      <div class="col-12">
    <div class="card setting-shell-card">
      <div class="card-header pb-0">
        <div class="tab-shell" role="tablist" aria-label="Team">
          <div
            class="tab-item"
            :class="{ active: activeTeam === 'a' }"
            role="tab"
            :aria-selected="activeTeam === 'a'"
            @click="switchTeam('a')"
          >
            TEAM A
          </div>
          <div
            class="tab-item"
            :class="{ active: activeTeam === 'b' }"
            role="tab"
            :aria-selected="activeTeam === 'b'"
            @click="switchTeam('b')"
          >
            TEAM B
          </div>
          <div
            class="tab-item"
            :class="{ active: activeTeam === 'c' }"
            role="tab"
            :aria-selected="activeTeam === 'c'"
            @click="switchTeam('c')"
          >
            TEAM C
          </div>
        </div>
      </div>

      <div class="card-body p-3">
        <div class="d-flex justify-content-end mb-3">
          <button
            type="button"
            class="btn btn-sm btn-dark mb-0"
            @click="openCreateModal"
          >
            <i class="fas fa-plus me-1"></i> Add Sprint
          </button>
        </div>

        <div v-if="loading" class="text-secondary text-sm py-3">Loading...</div>
        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs text-center">
                  Sprint
                </th>
                <th class="text-uppercase text-secondary text-xxs">
                  Sprint Name
                </th>
                <th class="text-uppercase text-secondary text-xxs">
                  Start Date
                </th>
                <th class="text-uppercase text-secondary text-xxs">End Date</th>
                <th class="text-uppercase text-secondary text-xxs text-center">
                  Weeks
                </th>
                <th class="text-uppercase text-secondary text-xxs text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td colspan="6" class="text-center py-5">
                  <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">
                    No sprint settings for Team {{ activeTeamLabel }}.
                  </p>
                </td>
              </tr>
              <tr v-for="row in rows" :key="row.sprintSettingId">
                <td class="text-sm text-center">{{ row.sprintRound ?? "—" }}</td>
                <td class="text-sm font-weight-bold">{{ row.sprintName || "—" }}</td>
                <td class="text-sm">{{ formatDisplayDate(row.startDate) }}</td>
                <td class="text-sm">{{ formatDisplayDate(row.endDate) }}</td>
                <td class="text-sm text-center">{{ row.weekCount }}</td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 action-edit me-2"
                    title="Edit"
                    @click="openEditModal(row)"
                  >
                    <i class="fas fa-edit" style="font-size: 0.85rem"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 action-delete"
                    title="Delete"
                    @click="removeRow(row)"
                  >
                    <i class="fas fa-trash-alt" style="font-size: 0.85rem"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
      </div>
    </div>

    <div
      v-if="showModal"
      class="modal-backdrop"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1040;
      "
         ></div>
    <div
      v-if="showModal"
      class="modal d-block"
      tabindex="-1"
      style="z-index: 1050"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">
              {{ editingId ? "Edit Sprint" : "Add Sprint" }} — Team
              {{ activeTeamLabel }}
            </h6>
          </div>
          <div class="modal-body">
            <div v-if="!editingId" class="mb-3">
              <label class="form-label text-xs">Sprint</label>
              <input
                :value="previewSprintRound"
                type="text"
                class="form-control form-control-sm"
                readonly
              />
            </div>
            <div class="mb-3">
              <label class="form-label text-xs">Sprint Name (optional)</label>
              <input
                v-model="form.sprintName"
                type="text"
                class="form-control form-control-sm"
                placeholder="e.g. Sprint 2"
              />
            </div>
            <div class="mb-3">
              <label class="form-label text-xs"
                >Week Setting <span class="text-danger">*</span></label
              >
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <input
                  v-model.number="form.weekCount"
                  type="number"
                  min="1"
                  max="52"
                  class="form-control form-control-sm"
                  style="max-width: 100px"
                  @change="applyWeeksToEndDate"
                />
                <span class="text-xs text-secondary">week(s)</span>
              </div>
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="form-label text-xs"
                  >Start Date <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.startDate"
                  type="date"
                  class="form-control form-control-sm"
                  @change="applyWeeksToEndDate"
                />
              </div>
              <div class="col-6 mb-3">
                <label class="form-label text-xs"
                  >End Date <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.endDate"
                  type="date"
                  class="form-control form-control-sm"
                  readonly
                />
              </div>
            </div>
            <div v-if="formError" class="login-error mt-1">
              <i class="fas fa-exclamation-circle"></i> {{ formError }}
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-sm btn-dark"
              :disabled="saving"
              @click="save"
            >
              <span v-if="saving"
                ><i class="fas fa-spinner fa-spin me-1"></i> Saving...</span
              >
              <span v-else>Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sprintSettingsApi } from "@/services/backendApi";

export default {
  name: "sprint-setting",
  data() {
    return {
      activeTeam: "a",
      loading: false,
      saving: false,
      rows: [],
      showModal: false,
      editingId: null,
      formError: null,
      form: {
        sprintName: "",
        startDate: "",
        endDate: "",
        weekCount: 1,
      },
    };
  },
  computed: {
    activeTeamLabel() {
      if (this.activeTeam === "b") return "B";
      if (this.activeTeam === "c") return "C";
      return "A";
    },
    previewSprintRound() {
      return this.computeNextSprintRound(this.form.startDate);
    },
  },
  async created() {
    await this.loadRows();
  },
  methods: {
    switchTeam(team) {
      this.activeTeam = team;
      this.loadRows();
    },
    async loadRows() {
      this.loading = true;
      try {
        this.rows = await sprintSettingsApi.getByTeam(this.activeTeam);
      } catch (e) {
        console.error("loadRows error:", e);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    formatDisplayDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      return `${d}/${m}/${y}`;
    },
    /**
     * Compute the sprint end date from the start date + week count.
     *
     * Business rule: sprints end on a working day. We add `weekCount * 7 - 1`
     * calendar days to the start (so a 2-week sprint covers 14 calendar days
     * including the start), and if the resulting date lands on a Saturday or
     * Sunday we roll back to the preceding Friday.
     *
     * Example:
     *   Start = 25 May 2026 (Mon), 2 weeks
     *     → +13 days = 7 Jun 2026 (Sun) → rolled back to 5 Jun 2026 (Fri).
     */
    endDateFromWeeks(startDate, weekCount) {
      if (!startDate || !weekCount) return "";
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(start);
      end.setDate(end.getDate() + Number(weekCount) * 7 - 1);
      // Roll Sat (6) / Sun (0) back to Friday
      const dow = end.getDay();
      if (dow === 6) end.setDate(end.getDate() - 1);
      else if (dow === 0) end.setDate(end.getDate() - 2);
      // Use local date components to avoid timezone shifts from toISOString
      const y = end.getFullYear();
      const m = String(end.getMonth() + 1).padStart(2, "0");
      const d = String(end.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    applyWeeksToEndDate() {
      const end = this.endDateFromWeeks(
        this.form.startDate,
        this.form.weekCount
      );
      if (end) this.form.endDate = end;
    },
    sprintYearFromStartDate(startDate) {
      if (!startDate) return new Date().getFullYear();
      const y = parseInt(String(startDate).substring(0, 4), 10);
      return Number.isFinite(y) ? y : new Date().getFullYear();
    },
    computeNextSprintRound(startDate) {
      const year = this.sprintYearFromStartDate(startDate);
      const maxRound = this.rows.reduce((max, row) => {
        const rowYear = this.sprintYearFromStartDate(row.startDate);
        if (rowYear !== year) return max;
        const round = Number(row.sprintRound);
        return Number.isFinite(round) ? Math.max(max, round) : max;
      }, 0);
      return maxRound + 1;
    },
    openCreateModal() {
      this.editingId = null;
      this.formError = null;
      this.form = {
        sprintName: "",
        startDate: "",
        endDate: "",
        weekCount: 1,
      };
      this.showModal = true;
    },
    openEditModal(row) {
      this.editingId = row.sprintSettingId;
      this.formError = null;
      this.form = {
        sprintName: row.sprintName || "",
        startDate: row.startDate || "",
        endDate: row.endDate || "",
        weekCount: Number(row.weekCount) || 1,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingId = null;
      this.formError = null;
    },
    async save() {
      this.formError = null;
      const payload = {
        team: this.activeTeam,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        weekCount: Number(this.form.weekCount),
      };
      const sprintName = this.form.sprintName.trim();
      if (sprintName) payload.sprintName = sprintName;
      if (!payload.startDate || !payload.endDate) {
        this.formError = "Please fill in start date and end date.";
        return;
      }
      if (!Number.isInteger(payload.weekCount) || payload.weekCount < 1) {
        this.formError = "Week setting must be at least 1.";
        return;
      }
      this.saving = true;
      try {
        if (this.editingId) {
          await sprintSettingsApi.update(this.editingId, payload);
        } else {
          await sprintSettingsApi.create(payload);
        }
        this.closeModal();
        await this.loadRows();
      } catch (e) {
        this.formError = e.message || "Failed to save";
      } finally {
        this.saving = false;
      }
    },
    async removeRow(row) {
      const label = row.sprintName || `Round ${row.sprintRound ?? "?"}`;
      if (!confirm(`Delete sprint "${label}"?`)) return;
      try {
        await sprintSettingsApi.delete(row.sprintSettingId);
        await this.loadRows();
      } catch (e) {
        alert(e.message || "Failed to delete");
      }
    },
  },
};
</script>

<style scoped>
.setting-shell-card {
  border-radius: 0.75rem;
}

.setting-shell-card .card-header {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

.login-error {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
}

.action-edit {
  color: #4361ee;
}

.action-edit:hover {
  color: #2d4bc8;
}

.action-delete {
  color: #e74c3c;
}

.action-delete:hover {
  color: #c0392b;
}
</style>
